import { usePos } from '../stores/posStore';

const { VITE_API_URL = 'http://localhost:3000' } = import.meta.env;

/**
 * Bearer and retry handling for the handful of endpoints that authorize the
 * *device* instead of the person standing at it - the POS push subscription and
 * the notification lists a device is on.
 *
 * Nothing schedules a POS token refresh: the device token is only ever rotated
 * when something trips over it. A device checked in this morning therefore hands
 * out an expired access token on the first device call of the afternoon, so
 * every call replays itself once behind a refresh - the same shape the PIN
 * login retry has in the auth store.
 */
export const usePosRequest = () => {
  const { posToken, canRefreshPos, refreshPosToken } = usePos();

  /**
   * The token goes through a getter so the header is built when the request
   * fires rather than when the hook is created, which is what makes the replay
   * below provably use the rotated token.
   */
  const posRequest: RequestInit = {
    get headers() {
      return { Authorization: `Bearer ${posToken.value}` };
    },
  };

  const withPosToken = async <TResult extends { status: number }>(
    call: () => Promise<TResult>,
  ): Promise<TResult> => {
    const result = await call();

    if (result.status !== 401 || !canRefreshPos.value) return result;

    // A failed refresh has already checked the device out; there is no token
    // left to replay the call with, so the original rejection stands.
    const refreshed = await refreshPosToken()
      .then(() => true)
      .catch(() => false);

    return refreshed ? call() : result;
  };

  return { posRequest, withPosToken };
};
